import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { extractErrorDetail } from "./validators";
import {
  Box, Typography, TextField, Button, Alert,
  CircularProgress, InputAdornment, IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import ThemeToggle from "./ThemeToggle";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

const B = {
  navy:     "#00357a",
  navyDark: "#001f4d",
  navyDeep: "#000e24",
  gold:     "#c9a96e",
  goldLight:"#dfc08a",
};

const FEATURES = [
  { icon: "🔬", text: "AI-powered diagnostic report extraction" },
  { icon: "🔒", text: "HIPAA-compliant, GCP BAA-covered infrastructure" },
  { icon: "📄", text: "Patient-friendly & clinician-grade PDF reports" },
  { icon: "☁️", text: "Instant cloud storage & signed-URL retrieval" },
];

export default function Login() {
  const navigate  = useNavigate();
  const theme     = useTheme();
  const isDark    = theme.palette.mode === "dark";

  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  const bg     = isDark ? "#080d1a" : "#f4f7ff";
  const cardBg = isDark ? "#111827" : "#ffffff";
  const textSub = isDark ? "#94a3b8" : "#64748b";

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.02)",
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,53,122,0.14)", borderRadius: "12px" },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.22)" : B.gold },
      "&.Mui-focused fieldset": { borderColor: B.gold, borderWidth: "1.5px" },
      "& input": { color: isDark ? "#f1f5f9" : "inherit", fontFamily: "'Inter', sans-serif", fontSize: "0.9rem" },
      "& input:-webkit-autofill": {
        WebkitBoxShadow: `0 0 0 100px ${isDark ? "#0f1b2d" : "#fff"} inset`,
        WebkitTextFillColor: isDark ? "#f1f5f9" : "inherit",
      },
    },
    "& .MuiInputLabel-root": { color: isDark ? "#94a3b8" : "#64748b", fontFamily: "'Inter', sans-serif" },
    "& .MuiInputLabel-root.Mui-focused": { color: B.gold },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return setError("Please enter your email and password.");
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.append("username", email.trim());
      params.append("password", password);
      const res = await axios.post(`${API}/api/auth/login`, params, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      localStorage.setItem("token", res.data.access_token);
      localStorage.setItem("user", JSON.stringify({ email: res.data.email, full_name: res.data.full_name }));
      // 2026-09-30 addition — admin-created accounts are forced to set
      // their own password on first login (force_password_change=true in
      // the login response).
      navigate(res.data.force_password_change ? "/change-password" : "/");
    } catch (err) {
      setError(extractErrorDetail(err, "Login failed. Please check your credentials."));
    }
    setLoading(false);
  };

  return (
    <Box sx={{
      minHeight: "100vh", bgcolor: bg,
      display: "flex", flexDirection: "column",
      overflow: "hidden", position: "relative",
      fontFamily: "'Inter', sans-serif",
    }}>
      {/* Ambient blobs */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-10%", left: "-5%",  size: 500, color: isDark ? "rgba(0,53,122,0.22)" : "rgba(0,53,122,0.07)" },
          { top: "60%",  right: "-8%", size: 400, color: isDark ? "rgba(201,169,110,0.09)" : "rgba(201,169,110,0.12)" },
          { top: "30%",  left: "50%",  size: 300, color: isDark ? "rgba(0,53,122,0.12)" : "rgba(0,53,122,0.05)" },
        ].map((b, i) => (
          <motion.div key={i} style={{
            position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size, borderRadius: "50%",
            background: b.color, filter: "blur(80px)",
          }}
            animate={{ scale: [1, 1.12, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 8 + i * 2, repeat: 2, delay: i * 0.8, ease: "easeInOut" }}
          />
        ))}
      </Box>

      {/* ── Navbar ── */}
      <motion.div initial={{ y: -64, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }} style={{ position: "relative", zIndex: 10 }}>
        <Box sx={{
          bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(0,31,77,0.96)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(201,169,110,0.2)",
        }}>
          <Box sx={{ height: 2, background: "linear-gradient(90deg, transparent, rgba(201,169,110,0.8) 30%, rgba(223,192,138,0.9) 50%, rgba(201,169,110,0.8) 70%, transparent)" }} />
          <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 68,
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
            <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 46, width: "auto" }} />
            <ThemeToggle />
          </Box>
        </Box>
      </motion.div>

      {/* ── Main content ── */}
      <Box sx={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        px: 3, py: 6, position: "relative", zIndex: 10,
        gap: { xs: 5, md: 10 }, flexDirection: { xs: "column", md: "row" },
      }}>
        {/* Left — branding */}
        <motion.div initial={{ x: -40, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}>
          <Box sx={{ maxWidth: 380, textAlign: { xs: "center", md: "left" } }}>
            {/* Eyebrow */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, justifyContent: { xs: "center", md: "flex-start" } }}>
              <Box sx={{ width: 32, height: 1, background: B.gold }} />
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: B.gold, letterSpacing: "0.28em", textTransform: "uppercase", fontFamily: "'Inter', sans-serif" }}>
                The Healthcare Decision Company
              </Typography>
            </Box>

            <Typography sx={{
              fontFamily: "'Playfair Display', serif",
              fontSize: { xs: 32, md: 44 }, fontWeight: 800,
              color: isDark ? "#f1f5f9" : B.navyDark, lineHeight: 1.1, mb: 2,
              letterSpacing: "-0.01em",
            }}>
              Labarium{" "}
              <Box component="span" sx={{
                background: `linear-gradient(135deg, ${B.gold} 0%, ${B.goldLight} 50%, ${B.gold} 100%)`,
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>
                Intelligence
              </Box>
            </Typography>

            <Typography sx={{ fontSize: 15, color: textSub, lineHeight: 1.8, mb: 4, fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>
              AI-powered diagnostic intelligence that transforms raw lab data into actionable insights — for clinicians and patients alike.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75,
              alignItems: { xs: "center", md: "flex-start" } }}>
              {FEATURES.map((f, i) => (
                <motion.div key={i} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 + i * 0.08, duration: 0.4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: "9px", flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      bgcolor: isDark ? "rgba(201,169,110,0.1)" : "rgba(0,53,122,0.06)",
                      border: `1px solid ${isDark ? "rgba(201,169,110,0.18)" : "rgba(0,53,122,0.1)"}`,
                      fontSize: 15,
                    }}>{f.icon}</Box>
                    <Typography sx={{ fontSize: 15, color: isDark ? "#cbd5e1" : "#334155", fontWeight: 500, fontFamily: "'Inter', sans-serif" }}>
                      {f.text}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>

            {/* Mission quote */}
            <Box sx={{
              mt: 4, pt: 3, borderTop: `1px solid ${isDark ? "rgba(201,169,110,0.15)" : "rgba(0,53,122,0.1)"}`,
              borderLeft: `3px solid ${B.gold}`, pl: 2,
            }}>
              <Typography sx={{ fontSize: 14.5, color: textSub, fontFamily: "'Playfair Display', serif", fontStyle: "italic", lineHeight: 1.75 }}>
                Health decisions are broken. <Box component="span" sx={{ color: B.gold, fontStyle: "normal", fontWeight: 700 }}>With Labarium, we will do better.</Box>
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* Right — login card */}
        <motion.div initial={{ y: 30, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.2, ease: [0.34, 1.56, 0.64, 1] }}>
          <Box sx={{
            width: { xs: "100%", sm: 440 }, maxWidth: 440,
            borderRadius: "24px", overflow: "hidden",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,53,122,0.1)"}`,
            boxShadow: isDark
              ? "0 32px 80px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)"
              : "0 32px 80px rgba(0,31,77,0.14), 0 4px 20px rgba(0,31,77,0.06)",
            bgcolor: cardBg, backdropFilter: "blur(20px)",
          }}>
            {/* Top stripe */}
            <Box sx={{ height: 3, background: `linear-gradient(to right, ${B.navyDeep}, ${B.navy}, ${B.gold})` }} />

            {/* Card header */}
            <Box sx={{
              bgcolor: B.navyDeep, px: 4.5, pt: 3.5, pb: 4,
              position: "relative", overflow: "hidden",
              background: `linear-gradient(135deg, ${B.navyDeep} 0%, ${B.navy} 100%)`,
            }}>
              <Box sx={{ position: "absolute", top: -45, right: -45, width: 150, height: 150,
                borderRadius: "50%", border: "1px solid rgba(201,169,110,0.12)" }} />
              <Box sx={{ position: "absolute", top: -22, right: -22, width: 95, height: 95,
                borderRadius: "50%", border: "1px solid rgba(201,169,110,0.18)" }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: B.gold,
                  "@keyframes lPulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
                  animation: "lPulse 2s infinite" }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.2em", color: B.gold, fontFamily: "'Inter', sans-serif" }}>
                  AUTHORISED ACCESS ONLY
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#fff", lineHeight: 1.2, fontFamily: "'Playfair Display', serif" }}>
                Sign in to Labarium
              </Typography>
              <Typography sx={{ fontSize: 14.5, color: "rgba(255,255,255,0.42)", mt: 0.5, fontFamily: "'Inter', sans-serif" }}>
                Access is logged and monitored
              </Typography>
            </Box>

            {/* Card body */}
            <Box sx={{ px: 4.5, py: 4 }}>
              <AnimatePresence mode="wait">
                {error && (
                  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                    <Alert severity="error" onClose={() => setError("")}
                      sx={{
                        mb: 3, borderRadius: "10px", fontSize: 14.5, fontFamily: "'Inter', sans-serif",
                        bgcolor: isDark ? "rgba(211,47,47,0.15)" : undefined,
                        color: isDark ? "#fca5a5" : undefined,
                        "& .MuiAlert-icon": { color: isDark ? "#fca5a5" : undefined },
                      }}>
                      {error}
                    </Alert>
                  </motion.div>
                )}
              </AnimatePresence>

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField label="Email address" type="email" size="small" fullWidth required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  sx={{ ...inputSx, mb: 2.5 }} autoFocus autoComplete="email" />
                <TextField label="Password" size="small" fullWidth required
                  type={showPw ? "text" : "password"}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  sx={{ ...inputSx, mb: 3.5 }} autoComplete="current-password"
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPw((p) => !p)} edge="end"
                        sx={{ color: isDark ? "#64748b" : "#94a3b8" }}>
                        {showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )}} />
                <motion.div whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 500, damping: 28 }}>
                  <Button type="submit" fullWidth variant="contained" disabled={loading}
                    sx={{
                      background: `linear-gradient(135deg, ${B.navyDeep} 0%, ${B.navy} 100%)`,
                      borderRadius: "12px", py: 1.5, fontWeight: 700,
                      textTransform: "none", fontSize: 15,
                      fontFamily: "'Inter', sans-serif", letterSpacing: "0.02em",
                      boxShadow: "0 4px 20px rgba(0,31,77,0.45)",
                      transition: "all 0.2s ease",
                      "&:hover": { boxShadow: "0 8px 28px rgba(0,31,77,0.55)", background: `linear-gradient(135deg, ${B.navyDeep} 0%, #004299 100%)` },
                      position: "relative", overflow: "hidden",
                      "@keyframes loginShimmer": { "0%": { left: "-80%" }, "100%": { left: "140%" } },
                      "&::after": !loading ? {
                        content: '""', position: "absolute",
                        top: 0, bottom: 0, left: "-80%", width: "50%",
                        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)",
                        transform: "skewX(-20deg)",
                        animation: "loginShimmer 3.5s ease-in-out infinite",
                      } : {},
                    }}>
                    {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Sign In"}
                  </Button>
                </motion.div>
              </Box>

              <Box sx={{ pt: 3.5, mt: 3, borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"}` }}>
                <Typography sx={{ fontSize: 13, color: isDark ? "#475569" : "#94a3b8",
                  textAlign: "center", lineHeight: 1.7, fontFamily: "'Inter', sans-serif" }}>
                  AI diagnostic insights do not constitute medical advice. Always consult a licensed provider.
                </Typography>
                {/* 2026-09-30 addition — admin login entry point */}
                <Typography sx={{ textAlign: "center", mt: 2 }}>
                  <Link to="/admin-login" style={{
                    fontSize: 13.5, fontWeight: 600, color: B.gold, textDecoration: "none",
                    fontFamily: "'Inter', sans-serif", letterSpacing: "0.02em",
                  }}>
                    Admin sign in →
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </Box>

      {/* Footer */}
      <Box sx={{
        bgcolor: isDark ? "rgba(2,6,15,0.92)" : B.navyDeep,
        backdropFilter: "blur(10px)",
        py: 2, textAlign: "center", position: "relative", zIndex: 10,
        borderTop: "1px solid rgba(201,169,110,0.12)",
      }}>
        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.18)", letterSpacing: "0.05em", fontFamily: "'Inter', sans-serif" }}>
          © {new Date().getFullYear()} LABARIUM DIAGNOSTIC SYSTEMS, INC. ALL RIGHTS RESERVED.
        </Typography>
      </Box>
    </Box>
  );
}
